import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconCautionMajorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCautionMajorSolid';
import { CobaltIconCheckmarkCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCheckmarkCircleSolid';
import { CobaltIconWarningMajorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconWarningMajorSolid';
import { classNames } from '@cae/cobalt-react/utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { FEATURE_FLAGS } from '@shared/constants/featureFlags.constants';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import type { OutstandingAmountTileProps } from './OutstandingAmountTile.types';

import styles from './OutstandingAmountTile.module.css';
import {
  formatAmountNumber,
  getTileDueDateVariant,
} from './OutstandingAmountTile.utils';

const OutstandingAmountTile = React.memo(
  ({
    outstandingAmount,
    currency,
    dueDate,
    balanceStatus,
    hasCreditMemos,
  }: Readonly<OutstandingAmountTileProps>): React.ReactElement => {
    const variant = getTileDueDateVariant(dueDate);
    const {
      [FEATURE_FLAGS.manageInvoicesCreditMemosEnabled]: manageInvoicesCreditMemosEnabled,
    } = useFlags();
    const isPastDue = variant === 'past-due';
    const isDueSoon = variant === 'due-soon';
    const formattedDate = formatDateShort(dueDate);
    const tileClassName = classNames(
      styles.tile,
      isPastDue
        ? styles.tilePastDue
        : isDueSoon
          ? styles.tileDueSoon
          : styles.tileDefault
    );

    if (manageInvoicesCreditMemosEnabled && balanceStatus === 'FullCredit') {
      return (
        <CobaltSectionMessage
          variant="info"
          data-testid="outstanding-amount-tile-full-credit"
          sectionMessageTitle={
            <h2 className={`body-02 ${styles.fullCreditText}`}>
              This invoice was fully credited, see details below.
            </h2>
          }
        />
      );
    }

    if (
      balanceStatus === 'Paid' ||
      (!manageInvoicesCreditMemosEnabled && balanceStatus === 'FullCredit')
    ) {
      return (
        <CobaltTile
          className={`${styles.tile} ${styles.tilePaid}`}
          data-testid="outstanding-amount-tile-paid"
        >
          <CobaltIconCheckmarkCircleSolid
            size="large"
            className={styles.paidIcon}
            aria-hidden="true"
          />
          <div>
            <div className="heading-01">Invoice paid.</div>
            {hasCreditMemos && (
              <div className="body-02">
                Note that a credit was applied to part of the balance, see
                details below.
              </div>
            )}
          </div>
        </CobaltTile>
      );
    }

    return (
      <CobaltTile
        className={tileClassName}
        data-testid="outstanding-amount-tile"
      >
        <p className={`heading-01 ${styles.label}`}>Outstanding amount</p>
        <div className={styles.amountRow}>
          {outstandingAmount != null ? (
            <>
              <span
                className={[
                  'heading-06',
                  isPastDue ? styles.amountPastDue : '',
                ].join(' ')}
                data-testid="outstanding-amount-tile-amount"
              >
                {formatAmountNumber(outstandingAmount)}
              </span>
              <span className={`heading-01 ${styles.currency}`}>{currency}</span>
            </>
          ) : (
            <span
              className={`heading-06 ${styles.amountEmpty}`}
              data-testid="outstanding-amount-tile-amount"
            >
              <DataValue value={null} />
            </span>
          )}
        </div>
        {dueDate ? (
          <div className={styles.dueDateRow}>
            {isDueSoon && (
              <CobaltIconCautionMajorSolid
                size="small"
                className={styles.iconDueSoon}
                aria-label="Due in the next 7 days"
              />
            )}
            {isPastDue && (
              <CobaltIconWarningMajorSolid
                size="small"
                className={styles.iconPastDue}
                aria-label="Past due"
              />
            )}
            <span
              className={`body-01 ${styles.dueDateText}`}
              data-testid="outstanding-amount-tile-due-date"
            >
              {isPastDue
                ? `Past due since ${formattedDate}`
                : `Due on ${formattedDate}`}
            </span>
          </div>
        ) : (
          <div className={styles.dueDateRow}>
            <span
              className={`body-01 ${styles.dueDateEmpty}`}
              data-testid="outstanding-amount-tile-due-date"
            >
              Due on <DataValue value={null} />
            </span>
          </div>
        )}
      </CobaltTile>
    );
  }
);

OutstandingAmountTile.displayName = 'OutstandingAmountTile';
export { OutstandingAmountTile };
